// Hook-local preview state for a streaming generate mutation. Feed `onChunk`
// (as consumeNdjsonStream's onChunk) the accumulated tool-input JSON; read:
//   items         — completed elements of the named top-level array so far
//   started       — whether any chunk has arrived (splits the pre-stream wait
//                   from "the model is writing" for two-phase status copy)
//   draftingTitle — the in-flight element's title once its string closes
//                   (title is the first field in all three tool schemas)
// State updates stay coarse — per item completed, plus once per item when its
// title lands — so the view re-renders as items materialise, not per token.

import { useCallback, useRef, useState } from 'react';
import { completedArrayItems, draftingItemTitle } from '@/lib/partial-json';

export function useStreamPreview<T>(arrayKey: string) {
  const [items, setItems] = useState<T[]>([]);
  const [started, setStarted] = useState(false);
  const [draftingTitle, setDraftingTitle] = useState<string | null>(null);
  const countRef = useRef(0);
  const scannedRef = useRef(0);
  const startedRef = useRef(false);

  const onChunk = useCallback(
    (accumulated: string) => {
      if (!startedRef.current) {
        startedRef.current = true;
        setStarted(true);
      }
      // An element can only complete on '}', a drafting title only on '"' —
      // skip the re-scan for deltas that contain neither (mid-string text).
      const delta = accumulated.slice(scannedRef.current);
      scannedRef.current = accumulated.length;
      if (!delta.includes('}') && !delta.includes('"')) return;
      const complete = completedArrayItems(accumulated, arrayKey);
      if (complete.length !== countRef.current) {
        countRef.current = complete.length;
        setItems(complete as T[]);
      }
      const title = draftingItemTitle(accumulated, arrayKey);
      setDraftingTitle((prev) => (prev === title ? prev : title));
    },
    [arrayKey],
  );

  const reset = useCallback(() => {
    countRef.current = 0;
    scannedRef.current = 0;
    startedRef.current = false;
    setItems([]);
    setStarted(false);
    setDraftingTitle(null);
  }, []);

  return { items, started, draftingTitle, onChunk, reset };
}
